import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import {
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Plus, Trash2, Loader2, Filter, Wallet
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  rental_payment: "Rental Payment",
  commission: "Commission",
  property_sale: "Property Sale",
  other_income: "Other Income",
  landlord_payment: "Landlord Payment",
  contractor: "Contractor",
  maintenance: "Maintenance",
  marketing: "Marketing",
  salaries: "Salaries",
  utilities: "Utilities",
  other_expense: "Other Expense",
};

const INFLOW_CATEGORIES = ["rental_payment", "commission", "property_sale", "other_income"];
const OUTFLOW_CATEGORIES = ["landlord_payment", "contractor", "maintenance", "marketing", "salaries", "utilities", "other_expense"];
const PIE_COLORS = ["#1a365d", "#38a169", "#d69e2e", "#e53e3e", "#3182ce", "#9f7aea", "#ed8936", "#63b3ed", "#f56565", "#48bb78", "#4299e1"];

const FinanceDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [period, setPeriod] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    type: "inflow", category: "", amount: "", description: "", reference: "", date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => { fetchAll(); }, [filterType, period]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("tx_type", filterType);
      params.append("limit", "200");

      const [txRes, sumRes] = await Promise.all([
        axios.get(`${API}/finance/transactions?${params}`, { withCredentials: true }),
        axios.get(`${API}/finance/summary?period=${period}`, { withCredentials: true }),
      ]);
      setTransactions(txRes.data.transactions || []);
      setTotal(txRes.data.total || 0);
      setSummary(sumRes.data);
    } catch (error) {
      console.error("Finance fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/finance/transactions`, {
        ...formData,
        amount: parseFloat(formData.amount),
      }, { withCredentials: true });
      toast.success("Transaction recorded");
      setDialogOpen(false);
      setFormData({ type: "inflow", category: "", amount: "", description: "", reference: "", date: new Date().toISOString().split("T")[0] });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add transaction");
    }
  };

  const handleDelete = async (txId) => {
    if (!window.confirm("Delete this transaction?")) return;
    setDeletingId(txId);
    try {
      await axios.delete(`${API}/finance/transactions/${txId}`, { withCredentials: true });
      toast.success("Transaction deleted");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !summary) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const catData = (summary?.category_breakdown || []).map((c, i) => ({
    ...c, label: CATEGORY_LABELS[c.category] || c.category, fill: PIE_COLORS[i % PIE_COLORS.length]
  }));

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif">Finance & Resources</h2>
          <p className="text-muted-foreground text-sm">Track business inflows, outflows, and net position</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 h-9" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-2" /> Add Transaction</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record Transaction</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, category: "" })}>
                    <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inflow">Inflow (Income)</SelectItem>
                      <SelectItem value="outflow">Outflow (Expense)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="tx-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {(formData.type === "inflow" ? INFLOW_CATEGORIES : OUTFLOW_CATEGORIES).map((cat) => (
                        <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount (K) *</label>
                  <Input required type="number" step="0.01" min="0.01" value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="10000" data-testid="tx-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <Textarea required value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} placeholder="e.g. Rent payment for Plot 123" data-testid="tx-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Reference</label>
                    <Input value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="INV-001" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="tx-submit-btn">Record Transaction</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border rounded-sm p-5">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-500 text-white p-2.5 rounded-lg"><ArrowUpRight className="w-5 h-5" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Inflows</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="total-inflow">K {summary.total_inflow.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-sm p-5">
            <div className="flex items-center gap-3">
              <div className="bg-red-500 text-white p-2.5 rounded-lg"><ArrowDownRight className="w-5 h-5" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Outflows</p>
                <p className="text-2xl font-bold text-red-600" data-testid="total-outflow">K {summary.total_outflow.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-sm p-5">
            <div className="flex items-center gap-3">
              <div className={`${summary.net_balance >= 0 ? 'bg-blue-500' : 'bg-orange-500'} text-white p-2.5 rounded-lg`}>
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Balance</p>
                <p className={`text-2xl font-bold ${summary.net_balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`} data-testid="net-balance">
                  K {summary.net_balance.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trend */}
          <div className="bg-card border rounded-sm p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" /> Monthly Cash Flow
            </h3>
            {summary.monthly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={summary.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="display" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `K${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `K ${v.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="inflow" name="Inflows" fill="#38a169" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outflow" name="Outflows" fill="#e53e3e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">No monthly data yet</div>
            )}
          </div>

          {/* Category Breakdown */}
          <div className="bg-card border rounded-sm p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" /> By Category
            </h3>
            {catData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={catData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} dataKey="amount" nameKey="label" label={false}>
                      {catData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `K ${v.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5 max-h-[200px] overflow-y-auto">
                  {catData.map((item) => (
                    <div key={item.category} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.fill }} />
                        <span className="truncate max-w-[120px]">{item.label}</span>
                      </div>
                      <span className="font-medium font-mono">K {item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">No category data yet</div>
            )}
          </div>
        </div>
      )}

      {/* Transaction List */}
      <div className="bg-card border rounded-sm">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Filter className="w-4 h-4" /> Transactions ({total})
          </h3>
          <div className="flex gap-1">
            {["all", "inflow", "outflow"].map((t) => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${filterType === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                data-testid={`filter-${t}`}>
                {t === "all" ? "All" : t === "inflow" ? "Inflows" : "Outflows"}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y max-h-[500px] overflow-y-auto">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
              <p>No transactions recorded yet</p>
            </div>
          ) : (
            transactions.map((tx) => (
              <div key={tx.transaction_id} className="flex items-center gap-4 p-4 hover:bg-muted/30" data-testid={`tx-row-${tx.transaction_id}`}>
                <div className={`p-2 rounded-lg ${tx.type === "inflow" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
                  {tx.type === "inflow" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{tx.description}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px] py-0">{CATEGORY_LABELS[tx.category] || tx.category}</Badge>
                    {tx.reference && <span className="text-[10px] text-muted-foreground font-mono">{tx.reference}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`font-semibold font-mono ${tx.type === "inflow" ? "text-emerald-600" : "text-red-600"}`}>
                    {tx.type === "inflow" ? "+" : "-"}K {tx.amount.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {tx.date ? new Date(tx.date).toLocaleDateString() : "-"}
                  </p>
                </div>
                <button onClick={() => handleDelete(tx.transaction_id)} disabled={deletingId === tx.transaction_id}
                  className="p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded hover:bg-destructive/10 disabled:opacity-50 shrink-0"
                  data-testid={`delete-tx-${tx.transaction_id}`} title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
